import { Form, Formik, FormikHelpers } from "formik";
import { sendNotificationValidation } from "../../helper/constant";
import { useDispatch, useSelector } from "react-redux";
import { StateInterface } from "../../helper/interface";
import { Listbox, Transition } from "@headlessui/react";
import { errorToast, successToast } from "../../helper/toast";
import { handleCatchResponse } from "../../helper/helper";
import { setLoading } from "../../store/slice/BaseSlice";
import { AxiosError } from "axios";
import Select, { MultiValue } from "react-select";
import { dataService } from "../../config/DataService";
import { api } from "../../config/Api";
import usePathName from "../../hooks/usePathName";
import useFetch from "../../hooks/useFetch";
import { useDropzone } from "react-dropzone";
import { Eye, Image as ImageIcon, Plus, RefreshCw, Trash2, X } from "react-feather";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import DeleteModal from "../modal/DeleteModal";
import Modal from "../modal/Modal";
import { ColInterface, IndustryForSelect } from "../../helper/interface";
import CustomTable from "../table/CustomTable";

enum USER_TYPE {
    BOTH = "",
    SCHEDULER = "Scheduler",
    RELIEVER = "Reliever",
}

const USER_TYPE_LABEL: Record<USER_TYPE, string> = {
    [USER_TYPE.BOTH]: "Select user type",
    [USER_TYPE.SCHEDULER]: "Scheduler",
    [USER_TYPE.RELIEVER]: "Casual",
};

enum DEVICE_TYPE {
    BOTH = "",
    ANDROID = "android",
    IOS = "ios",
}

type FeedAudience = "scheduler" | "casual" | "both";

const FEED_AUDIENCES: { value: FeedAudience; label: string; description: string }[] = [
    { value: "scheduler", label: "Scheduler", description: "Only scheduler accounts" },
    { value: "casual", label: "Casual", description: "Only casual accounts" },
    { value: "both", label: "Both", description: "Scheduler and casual accounts" },
];

const feedAudienceLabel = (audience: FeedAudience) => FEED_AUDIENCES.find((option) => option.value === audience)?.label ?? "Both";
const MAX_FEED_CONTENT_LENGTH = 255;

interface NotificationForm {
    title: string;
    description: string;
    deviceType: DEVICE_TYPE;
    userType: USER_TYPE;
}

interface FeedMedia {
    url: string;
    type: "image" | "video";
    name: string;
}

interface FeedPost {
    feedPostId: number;
    content: string | null;
    media: FeedMedia[];
    audience: FeedAudience;
    industryIds: number[] | null;
    createdAt: string;
}

interface PendingFeedMedia {
    file: File;
    previewUrl: string;
    type: "image" | "video";
}

const isVideoFile = (file: File) => file.type.startsWith("video/") || /\.(mp4|mov)$/i.test(file.name);

const NotificationPanel = () => {
    const loading = useSelector((state: StateInterface) => state.base.loading);
    const dispatch = useDispatch();

    const onSubmit = async (values: NotificationForm, { resetForm }: FormikHelpers<NotificationForm>) => {
        try {
            dispatch(setLoading(true));

            const payload = {
                title: values.title,
                description: values.description,
                deviceType: values.deviceType,
                userType: values.userType,
            };

            const response = await dataService.post(api.admin.sendNotification, payload);

            successToast(response.data?.message);
        } catch (error) {
            handleCatchResponse(error as AxiosError);
        } finally {
            resetForm();
            dispatch(setLoading(false));
        }
    };

    return (
        <div className="intro-y box py-10 sm:py-10 mt-5">
            <div className="flex w-full items-center">
                <div className="flex-1 font-medium text-center text-lg -ml-2">Send notification</div>
            </div>
            <div className="px-5 sm:px-20 mt-10 pt-10 border-t border-slate-200/60 dark:border-darkmode-400">
                <Formik
                    initialValues={{
                        title: "",
                        description: "",
                        deviceType: DEVICE_TYPE.BOTH,
                        userType: USER_TYPE.BOTH,
                    }}
                    validationSchema={sendNotificationValidation}
                    onSubmit={onSubmit}
                >
                    {({ handleChange, handleBlur, values, errors, touched, dirty, setFieldTouched, setFieldValue }) => (
                        <Form>
                            <div className="grid grid-cols-12 gap-4 gap-y-5 mt-5">
                                <div className="intro-y col-span-12 sm:col-span-6 position-custom">
                                    <label className="mb-2 form-label">Device type</label>
                                    <div className="tom-select">
                                        <Listbox
                                            value={values.deviceType}
                                            onChange={(value: string) => {
                                                setFieldValue("deviceType", value);
                                            }}
                                            disabled={loading}
                                        >
                                            <Listbox.Button className="form-control ts-input">
                                                <div className="item">{values?.deviceType || "Select device type"}</div>
                                            </Listbox.Button>
                                            <Transition
                                                enter="transition duration-100 ease-out"
                                                enterFrom="transform scale-95 opacity-0"
                                                enterTo="transform scale-100 opacity-100"
                                                leave="transition duration-75 ease-out"
                                                leaveFrom="transform scale-100 opacity-100"
                                                leaveTo="transform scale-95 opacity-0"
                                                onBlur={() => setFieldTouched("deviceType", true)}
                                            >
                                                <Listbox.Options className="ts-dropdown single w-full">
                                                    <div role="listbox" className="ts-dropdown-content">
                                                        {Object.values(DEVICE_TYPE).map((data) => (
                                                            <Listbox.Option
                                                                className={({ active }) =>
                                                                    `relative cursor-default select-none py-2 pl-3 pr-4 ${
                                                                        active || values.deviceType === data ? "optionColor" : null
                                                                    }`
                                                                }
                                                                key={data}
                                                                value={data}
                                                            >
                                                                {data || "Select device type"}
                                                            </Listbox.Option>
                                                        ))}
                                                    </div>
                                                </Listbox.Options>
                                            </Transition>
                                        </Listbox>
                                    </div>
                                    {errors.deviceType && touched.deviceType && <div className="text-danger">{errors.deviceType}</div>}
                                </div>
                                <div className="intro-y col-span-12 sm:col-span-6 position-custom">
                                    <label className="mb-2 form-label">User type</label>
                                    <div className="tom-select">
                                        <Listbox
                                            value={values.userType}
                                            onChange={(value: string) => {
                                                setFieldValue("userType", value);
                                            }}
                                            disabled={loading}
                                        >
                                            <Listbox.Button className="form-control ts-input">
                                                <div className="item">{USER_TYPE_LABEL[values?.userType] ?? "Select user type"}</div>
                                            </Listbox.Button>
                                            <Transition
                                                enter="transition duration-100 ease-out"
                                                enterFrom="transform scale-95 opacity-0"
                                                enterTo="transform scale-100 opacity-100"
                                                leave="transition duration-75 ease-out"
                                                leaveFrom="transform scale-100 opacity-100"
                                                leaveTo="transform scale-95 opacity-0"
                                                onBlur={() => setFieldTouched("userType", true)}
                                            >
                                                <Listbox.Options className="ts-dropdown single w-full">
                                                    <div role="listbox" className="ts-dropdown-content">
                                                        {Object.values(USER_TYPE).map((data) => (
                                                            <Listbox.Option
                                                                className={({ active }) =>
                                                                    `relative cursor-default select-none py-2 pl-3 pr-4 ${
                                                                        active || values.userType === data ? "optionColor" : null
                                                                    }`
                                                                }
                                                                key={data}
                                                                value={data}
                                                            >
                                                                {USER_TYPE_LABEL[data] || "Select user type"}
                                                            </Listbox.Option>
                                                        ))}
                                                    </div>
                                                </Listbox.Options>
                                            </Transition>
                                        </Listbox>
                                    </div>
                                    {errors.userType && touched.userType && <div className="text-danger">{errors.userType}</div>}
                                </div>
                                <div className="intro-y col-span-12 sm:col-span-6">
                                    <label htmlFor="title" className="form-label">
                                        Title
                                    </label>
                                    <input
                                        id="title"
                                        name="title"
                                        type="text"
                                        onChange={handleChange}
                                        value={values.title}
                                        onBlur={handleBlur}
                                        className={`form-control ${errors.title && touched.title && "border-danger"}`}
                                        placeholder="Enter your notification title here"
                                    />
                                    {errors.title && touched.title && <div className="text-danger">{errors.title}</div>}
                                </div>
                                <div className="intro-y col-span-12 sm:col-span-6">
                                    <label htmlFor="description" className="form-label">
                                        Description
                                    </label>
                                    <input
                                        id="description"
                                        name="description"
                                        type="text"
                                        onChange={handleChange}
                                        value={values.description}
                                        onBlur={handleBlur}
                                        className={`form-control ${errors.description && touched.description && "border-danger"}`}
                                        placeholder="Enter your description title here"
                                    />
                                    {errors.description && touched.description && <div className="text-danger">{errors.description}</div>}
                                </div>
                                <div className="intro-y col-span-12 flex items-center justify-center sm:justify-end mt-5">
                                    <button className="btn btn-secondary w-24" type="reset" disabled={!dirty}>
                                        Reset
                                    </button>
                                    <button className="btn btn-primary w-24 ml-2" type="submit" disabled={loading || !dirty}>
                                        Send
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

const FeedPanel = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state: StateInterface) => state.base.loading);
    const [content, setContent] = useState("");
    const [audience, setAudience] = useState<FeedAudience>("both");
    const [industries, setIndustries] = useState<IndustryForSelect[]>([]);
    const [files, setFiles] = useState<PendingFeedMedia[]>([]);
    const [feedPosts, setFeedPosts] = useState<FeedPost[]>([]);
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [feedPage, setFeedPage] = useState(1);
    const [feedLimit, setFeedLimit] = useState(10);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [feedPostToDelete, setFeedPostToDelete] = useState<number | null>(null);
    const [feedPostToView, setFeedPostToView] = useState<FeedPost | null>(null);
    const pendingMediaRef = useRef<PendingFeedMedia[]>([]);
    const industryOptions = useFetch<IndustryForSelect[]>(api.admin.getAllIndustryForSelect);

    const clearPendingMedia = () => {
        pendingMediaRef.current.forEach((media) => URL.revokeObjectURL(media.previewUrl));
        pendingMediaRef.current = [];
        setFiles([]);
    };

    const fetchFeedPosts = async () => {
        try {
            setIsFetching(true);
            const response = await dataService.get(api.admin.feed);
            setFeedPosts(response.data?.data || []);
        } catch (error) {
            handleCatchResponse(error as AxiosError);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        fetchFeedPosts();
    }, []);

    useEffect(() => {
        return () => {
            pendingMediaRef.current.forEach((media) => URL.revokeObjectURL(media.previewUrl));
        };
    }, []);

    const feedMaxPage = Math.max(Math.ceil(feedPosts.length / feedLimit), 1);
    const paginatedFeedPosts = feedPosts.slice((feedPage - 1) * feedLimit, feedPage * feedLimit);

    useEffect(() => {
        setFeedPage((currentPage) => Math.min(currentPage, feedMaxPage));
    }, [feedMaxPage]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "video/mp4": [".mp4"],
            "video/quicktime": [".mov"],
            "video/x-quicktime": [".mov"],
        },
        maxFiles: 6,
        maxSize: 30 * 1024 * 1024,
        multiple: true,
        onDrop: (acceptedFiles) => {
            const newMedia = acceptedFiles.map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
                type: isVideoFile(file) ? "video" : "image",
            })) as PendingFeedMedia[];

            setFiles((currentFiles) => {
                const nextMedia = [...currentFiles, ...newMedia].slice(0, 6);
                newMedia.slice(Math.max(0, 6 - currentFiles.length)).forEach((media) => URL.revokeObjectURL(media.previewUrl));
                pendingMediaRef.current = nextMedia;
                return nextMedia;
            });
        },
        onDropRejected: () => errorToast("Upload up to six JPG, JPEG, PNG, MP4, or MOV files. Each file must be 30 MB or smaller."),
    });

    const publishFeedPost = async () => {
        if (!content.trim() && files.length === 0) {
            errorToast("Add text or at least one image or video to publish a Feed item.");
            return;
        }

        try {
            dispatch(setLoading(true));
            const formData = new FormData();
            formData.append("content", content.trim());
            formData.append("audience", audience);
            formData.append("industryIds", JSON.stringify(industries.map((industry) => industry.industryId)));
            files.forEach(({ file }) => formData.append("media", file));
            const response = await dataService.post(api.admin.feed, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            successToast(response.data?.message);
            setContent("");
            setAudience("both");
            setIndustries([]);
            clearPendingMedia();
            setIsComposerOpen(false);
            await fetchFeedPosts();
        } catch (error) {
            handleCatchResponse(error as AxiosError);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const deleteFeedPost = async () => {
        if (feedPostToDelete === null) return;

        try {
            setDeletingId(feedPostToDelete);
            const response = await dataService.delete(`${api.admin.feed}/${feedPostToDelete}`);
            successToast(response.data?.message);
            setFeedPosts((posts) => posts.filter((post) => post.feedPostId !== feedPostToDelete));
        } catch (error) {
            handleCatchResponse(error as AxiosError);
        } finally {
            setDeletingId(null);
            setFeedPostToDelete(null);
        }
    };

    const feedColumns: ColInterface<FeedPost>[] = [
        {
            title: "#",
            key: "feedPostId",
            render: (_post, index) => <span>{index}</span>,
        },
        {
            title: "MESSAGE",
            key: "content",
            render: (post) => (
                <span
                    className="feed-message-preview"
                    data-tooltip-id="feed-message-tooltip"
                    data-tooltip-content={post.content || "Media-only post"}
                >
                    {post.content || "Media-only post"}
                </span>
            ),
        },
        {
            title: "AUDIENCE",
            key: "audience",
            render: (post) => <span>{feedAudienceLabel(post.audience)}</span>,
        },
        {
            title: "INDUSTRIES",
            key: "industryIds",
            render: (post) => <span>{post.industryIds?.length ? `${post.industryIds.length} selected` : "All"}</span>,
        },
        {
            title: "MEDIA",
            key: "media",
            render: (post) => {
                const mediaCount = post.media?.length ?? 0;
                return <span>{mediaCount ? `${mediaCount} attachment${mediaCount === 1 ? "" : "s"}` : "-"}</span>;
            },
        },
        {
            title: "PUBLISHED",
            key: "createdAt",
            render: (post) => <span>{moment(post.createdAt).format("DD MMM YYYY, h:mm A")}</span>,
        },
        {
            title: "ACTION",
            key: "action",
            render: (post) => (
                <div className="flex items-center justify-center gap-3">
                    <button type="button" className="flex items-center text-primary" onClick={() => setFeedPostToView(post)}>
                        <Eye className="w-4 h-4 mr-1" /> View
                    </button>
                    <button
                        type="button"
                        className="flex items-center text-danger"
                        disabled={deletingId === post.feedPostId}
                        onClick={() => setFeedPostToDelete(post.feedPostId)}
                    >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <DeleteModal
                handleCancel={() => setFeedPostToDelete(null)}
                isOpen={feedPostToDelete !== null}
                onDelete={deleteFeedPost}
                subTitle="Do you really want to delete this Feed item?"
            />
            <Modal isOpen={feedPostToView !== null} size="xl">
                <section className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 8rem)" }}>
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200/60">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                            <Eye size={20} />
                        </div>
                        <div>
                            <h2 className="font-medium text-lg">Feed item</h2>
                            <p className="text-slate-500 text-sm mt-0.5">Published {feedPostToView && moment(feedPostToView.createdAt).format("DD MMM YYYY, h:mm A")}</p>
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm ml-auto" onClick={() => setFeedPostToView(null)}>
                            Close
                        </button>
                    </div>
                    {feedPostToView && (
                        <div className="p-5">
                            <div className="grid grid-cols-3 gap-3 text-sm mb-5">
                                <div>
                                    <span className="block text-slate-500">Audience</span>
                                    <span className="font-medium">{feedAudienceLabel(feedPostToView.audience)}</span>
                                </div>
                                <div>
                                    <span className="block text-slate-500">Industries</span>
                                    <span className="font-medium">{feedPostToView.industryIds?.length ? `${feedPostToView.industryIds.length} selected` : "All"}</span>
                                </div>
                                <div>
                                    <span className="block text-slate-500">Attachments</span>
                                    <span className="font-medium">{feedPostToView.media?.length ?? 0}</span>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-medium mb-2">Message</h3>
                                <p className="whitespace-pre-wrap text-slate-700">{feedPostToView.content || "No message was added to this Feed item."}</p>
                            </div>
                            {feedPostToView.media?.length > 0 && (
                                <div className="mt-5">
                                    <h3 className="font-medium mb-2">Attachments</h3>
                                    <div
                                        className={`feed-post-media grid gap-3 ${feedPostToView.media.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
                                        style={{ gridAutoRows: "16rem" }}
                                    >
                                        {feedPostToView.media.map((media, index) =>
                                            media.type === "image" ? (
                                                <img key={`${media.url}-${index}`} src={media.url} alt={media.name} className="h-full w-full rounded-md bg-slate-100" />
                                            ) : (
                                                <video key={`${media.url}-${index}`} src={media.url} controls className="h-full w-full rounded-md bg-black" />
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </Modal>
            <Modal isOpen={isComposerOpen} size="xl">
                <section className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 8rem)" }}>
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200/60">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                            <Plus size={20} />
                        </div>
                        <div>
                            <h2 className="font-medium text-lg">Create a Feed post</h2>
                            <p className="text-slate-500 text-sm mt-0.5">Choose who can see this update before publishing.</p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm ml-auto"
                            onClick={() => setIsComposerOpen(false)}
                            disabled={loading}
                        >
                        Cancel
                        </button>
                    </div>
                    <div className="p-5">
                        <label htmlFor="feed-content" className="form-label font-medium">What's new?</label>
                        <textarea
                            id="feed-content"
                            className="form-control resize-none"
                            rows={5}
                            maxLength={MAX_FEED_CONTENT_LENGTH}
                            value={content}
                            onChange={(event) => setContent(event.target.value)}
                            placeholder="Write an update for the Feed..."
                            disabled={loading}
                        />
                        <fieldset className="mt-5">
                            <legend className="form-label font-medium mb-2">Audience</legend>
                            <div className="grid grid-cols-3 gap-2">
                                {FEED_AUDIENCES.map((option) => {
                                    const selected = audience === option.value;
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            aria-pressed={selected}
                                            disabled={loading}
                                            onClick={() => setAudience(option.value)}
                                            className={`rounded-md border p-3 text-left transition-colors ${
                                                selected
                                                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                                                    : "border-slate-200 hover:border-primary/50 hover:bg-slate-50"
                                            }`}
                                        >
                                            <span className={`block text-sm font-medium ${selected ? "text-primary" : "text-slate-700"}`}>{option.label}</span>
                                            <span className="block text-xs text-slate-500 mt-1 leading-snug">{option.description}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>
                        <div className="mt-5">
                            <label className="form-label font-medium">Industries <span className="text-slate-400 font-normal">(optional)</span></label>
                            <p className="text-slate-500 text-sm mb-2">Leave empty to show this Feed item to every industry in the selected audience.</p>
                            <Select
                                isMulti
                                isDisabled={loading || !industryOptions.res?.data}
                                options={industryOptions.res?.data || []}
                                value={industries}
                                onChange={(selectedIndustries: MultiValue<IndustryForSelect>) => setIndustries([...selectedIndustries])}
                                className="tom-select"
                                classNamePrefix="select"
                                placeholder="All industries"
                                getOptionLabel={(industry) => industry.name}
                                getOptionValue={(industry) => String(industry.industryId)}
                            />
                        </div>
                        <div className="mt-5">
                            <label className="form-label font-medium">Add media <span className="text-slate-400 font-normal">(optional)</span></label>
                            <div {...getRootProps()} className={`dropzone cursor-pointer py-6 ${isDragActive ? "border-primary" : ""}`}>
                                <input {...getInputProps()} />
                                <div className="dz-message">
                                    <ImageIcon size={28} className="mx-auto mb-2 text-primary" />
                                    <div className="font-medium">Add photos or short videos</div>
                                    <div className="text-slate-500 text-sm mt-1">Up to 6 JPG, PNG, MP4, or MOV files (30 MB each).</div>
                                </div>
                            </div>
                            {files.length > 0 && (
                                <div className="mt-3 grid grid-cols-2 gap-3">
                                    {files.map((media, index) => (
                                        <div key={`${media.file.name}-${index}`} className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                                            {media.type === "image" ? (
                                                <img src={media.previewUrl} alt={media.file.name} className="block h-auto w-full" />
                                            ) : (
                                                <video src={media.previewUrl} controls className="h-28 w-full bg-black object-cover" />
                                            )}
                                            <button
                                                type="button"
                                                className="absolute right-2 top-2 rounded-full bg-white p-1 text-slate-600 shadow hover:text-danger"
                                                aria-label={`Remove ${media.file.name}`}
                                                onClick={() => {
                                                    URL.revokeObjectURL(media.previewUrl);
                                                    setFiles((currentFiles) => {
                                                        const nextMedia = currentFiles.filter((_, fileIndex) => fileIndex !== index);
                                                        pendingMediaRef.current = nextMedia;
                                                        return nextMedia;
                                                    });
                                                }}
                                            >
                                                <X size={18} />
                                            </button>
                                            <div className="flex items-center px-3 py-2">
                                                <ImageIcon size={15} className="shrink-0 text-slate-500 mr-2" />
                                                <span className="truncate text-sm text-slate-600">{media.file.name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-200/60">
                            <span className="text-slate-500 text-sm">{content.length}/{MAX_FEED_CONTENT_LENGTH} characters</span>
                            <button
                                className="btn btn-primary"
                                type="button"
                                disabled={loading || (!content.trim() && files.length === 0)}
                                onClick={publishFeedPost}
                            >
                                <Plus size={16} className="mr-1" /> Publish Feed
                            </button>
                        </div>
                    </div>
                </section>
            </Modal>
            <CustomTable
                cols={feedColumns}
                data={paginatedFeedPosts}
                tableKey="feedPostId"
                currentOrder={null}
                onSetOrderBy={() => undefined}
                loading={isFetching}
                error={null}
                tableOffset={{
                    total: feedPosts.length,
                    page: feedPage,
                    limit: feedLimit,
                    maxPage: feedMaxPage,
                    onNext: () => setFeedPage((currentPage) => Math.min(currentPage + 1, feedMaxPage)),
                    onPrevious: () => setFeedPage((currentPage) => Math.max(currentPage - 1, 1)),
                    onSetPage: setFeedPage,
                    onSetLimit: (limit) => {
                        setFeedLimit(limit);
                        setFeedPage(1);
                    },
                }}
                component={
                    <div className="flex w-full items-center justify-between gap-3">
                        <div>
                            <h2 className="font-medium text-lg">Published Feed</h2>
                            <p className="text-slate-500 text-sm mt-0.5">Newest posts appear first in the app.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={fetchFeedPosts} disabled={isFetching}>
                                <RefreshCw size={14} className={`mr-1 ${isFetching ? "animate-spin" : ""}`} /> Refresh
                            </button>
                            <button className="btn btn-primary btn-sm" type="button" onClick={() => setIsComposerOpen(true)} disabled={loading}>
                                <Plus size={16} className="mr-1" /> Add Feed
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

const Notification = () => {
    const [activeTab, setActiveTab] = useState<"feed" | "notification">("feed");

    usePathName([{ pathName: "Notification", href: "/notification" }]);

    return (
        <div>
            <div className="box">
                <ul className="nav nav-boxed-tabs mt-8" role="tablist">
                    <li className="nav-item flex-1" role="presentation">
                        <button
                            className={`nav-link w-full py-2 ${activeTab === "feed" ? "active" : ""}`}
                            data-tw-toggle="pill"
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "feed"}
                            onClick={() => setActiveTab("feed")}
                        >
                            Feed
                        </button>
                    </li>
                    <li className="nav-item flex-1" role="presentation">
                        <button
                            className={`nav-link w-full py-2 ${activeTab === "notification" ? "active" : ""}`}
                            data-tw-toggle="pill"
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "notification"}
                            onClick={() => setActiveTab("notification")}
                        >
                            Notification
                        </button>
                    </li>
                </ul>
            </div>
            <div className="tab-content mt-5">{activeTab === "feed" ? <FeedPanel /> : <NotificationPanel />}</div>
        </div>
    );
};

export default Notification;
